import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import { Input, Select, NumberInput } from '../../components/ui/Input';

import { createBudget } from '../../api/budgets';
import { extractErrorMessage } from '../../api/client';
import { DEPARTMENTS } from '../../auth/permissions';

const DEPARTMENT_OPTIONS = Object.values(DEPARTMENTS);
const QUARTERS = [1, 2, 3, 4];

const EMPTY = {
  department: '',
  projectId: '',
  category: '',
  allocatedAmount: '',
  year: new Date().getFullYear(),
  quarter: 1,
};

/**
 * Modal-based create form for a Budget. Budget creation is idempotent on
 * the backend for (department, category, projectId, year, quarter); we
 * surface the resulting record either way.
 */
export default function BudgetForm({ open, onClose, onCreated, defaultYear, defaultDepartment }) {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const base = defaultYear || currentYear;
    return [base - 2, base - 1, base, base + 1, base + 2];
  }, [defaultYear, currentYear]);

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        year: defaultYear || currentYear,
        department: defaultDepartment || '',
      });
      setErrors({});
    }
  }, [open, defaultYear, defaultDepartment, currentYear]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        department: form.department,
        projectId: form.projectId ? Number(form.projectId) : null,
        category: form.category.trim(),
        allocatedAmount: Number(form.allocatedAmount),
        year: Number(form.year),
        quarter: Number(form.quarter),
      };
      return createBudget(payload);
    },
    onSuccess: (created) => {
      toast.success('Budget saved');
      qc.invalidateQueries({ queryKey: ['budgets'] });
      onCreated?.(created);
      onClose?.();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validate() {
    const next = {};
    if (!form.department) next.department = 'Department is required';
    if (!form.category.trim()) next.category = 'Category is required';
    if (!form.allocatedAmount || Number(form.allocatedAmount) <= 0) {
      next.allocatedAmount = 'Allocated amount must be greater than zero';
    }
    if (!form.year) next.year = 'Year is required';
    if (!form.quarter) next.quarter = 'Quarter is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New budget"
      description="Allocate funds to a department, category, and quarter."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={onSubmit}
          >
            Create budget
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label="Department" required error={errors.department}>
          <Select
            value={form.department}
            onChange={(e) => update({ department: e.target.value })}
            error={!!errors.department}
          >
            <option value="">Select department…</option>
            {DEPARTMENT_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Category" required error={errors.category}>
          <Input
            value={form.category}
            onChange={(e) => update({ category: e.target.value })}
            placeholder="e.g. TRAVEL, SOFTWARE"
            error={!!errors.category}
            autoFocus
          />
        </FormField>

        <FormField label="Allocated amount (INR)" required error={errors.allocatedAmount}>
          <NumberInput
            min="0"
            step="0.01"
            value={form.allocatedAmount}
            onChange={(e) => update({ allocatedAmount: e.target.value })}
            error={!!errors.allocatedAmount}
            placeholder="0.00"
          />
        </FormField>

        <FormField label="Project ID" hint="Optional — internal project reference.">
          <NumberInput
            min="0"
            step="1"
            value={form.projectId}
            onChange={(e) => update({ projectId: e.target.value })}
            placeholder="e.g. 1042"
          />
        </FormField>

        <FormField label="Year" required error={errors.year}>
          <Select
            value={form.year}
            onChange={(e) => update({ year: Number(e.target.value) })}
            error={!!errors.year}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Quarter" required error={errors.quarter}>
          <Select
            value={form.quarter}
            onChange={(e) => update({ quarter: Number(e.target.value) })}
            error={!!errors.quarter}
          >
            {QUARTERS.map((q) => (
              <option key={q} value={q}>
                Q{q}
              </option>
            ))}
          </Select>
        </FormField>
      </form>
    </Modal>
  );
}
